import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { type SuggestionProps } from "@tiptap/suggestion";
import { atom } from "jotai";
import type {
  TagSearchResult,
  TagAutocompleteState,
} from "@pineseed/gui/types";
import { CustomMention } from "../extensions/custom-mention";

/**
 * Tiptap Text Input Handler
 * STRICT ARCHITECTURE COMPLIANCE: Business logic only
 * - No UI code
 * - No direct state mutations
 * - Communicates with state via callbacks
 * - Manages Tiptap editor instance and logic
 */

// Handler-specific state atoms
export const tiptapEditorAtom = atom<Editor | null>(null);
export const tiptapAutocompleteStateAtom = atom<TagAutocompleteState>({
  isOpen: false,
  query: "",
  results: [],
  selectedIndex: 0,
  position: { x: 0, y: 0 },
  placement: {
    horizontal: "left",
    vertical: "below",
  },
});

// Tiptap document structure types (compatible with JSONContent)
interface TiptapMentionNode {
  type: "mention";
  attrs?: {
    id: string;
    label: string;
  };
}

interface TiptapTextNode {
  type: "text";
  text?: string;
}

type TiptapNode = {
  type?: string;
  content?: TiptapNode[];
  attrs?: Record<string, unknown>;
  text?: string;
};

// Callback interfaces for handler communication
interface TiptapHandlerCallbacks {
  onStateChange?: (state: TagAutocompleteState) => void;
  onTagSearch?: (query: string) => TagSearchResult[];
  onTagCreate?: (name: string) => TagSearchResult | null;
  onDraftTextChange?: (text: string) => void;
}

// Proper Tiptap suggestion types
type MentionSuggestionProps = SuggestionProps<TagSearchResult[]>;

interface MentionKeyDownProps {
  event: KeyboardEvent;
}

export class TiptapTextInputHandler {
  private static instance: TiptapTextInputHandler;
  private editor: Editor | null = null;
  private onStateChange?: (state: TagAutocompleteState) => void;
  private onTagSearch?: (query: string) => TagSearchResult[];
  private onTagCreate?: (name: string) => TagSearchResult | null;
  private onDraftTextChange?: (text: string) => void;
  private draftSaveTimeout?: number;
  private currentSuggestionProps: MentionSuggestionProps | null = null;
  private currentSelectedIndex: number = 0;
  private currentAutocompleteState: TagAutocompleteState | null = null;

  public static getInstance(): TiptapTextInputHandler {
    if (!TiptapTextInputHandler.instance) {
      TiptapTextInputHandler.instance = new TiptapTextInputHandler();
    }
    return TiptapTextInputHandler.instance;
  }

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Set up callbacks for communication with state layer
   */
  public setCallbacks(callbacks: TiptapHandlerCallbacks) {
    this.onStateChange = callbacks.onStateChange;
    this.onTagSearch = callbacks.onTagSearch;
    this.onTagCreate = callbacks.onTagCreate;
    this.onDraftTextChange = callbacks.onDraftTextChange;
  }

  /**
   * Save draft text with debouncing
   */
  private saveDraftText(text: string) {
    if (this.draftSaveTimeout) {
      clearTimeout(this.draftSaveTimeout);
    }

    this.draftSaveTimeout = window.setTimeout(() => {
      this.onDraftTextChange?.(text);
    }, 500);
  }

  /**
   * Cancel any pending draft saves
   */
  public cancelDraftSave() {
    if (this.draftSaveTimeout) {
      clearTimeout(this.draftSaveTimeout);
      this.draftSaveTimeout = undefined;
    }
  }

  /**
   * Create Tiptap editor configuration
   * Returns configuration object for useEditor hook
   */
  public createEditorConfig() {
    return {
      extensions: [
        StarterKit.configure({
          history: {
            depth: 50,
          },
        }),
        CustomMention.configure({
          HTMLAttributes: {
            class: "mention-tag",
          },
          suggestion: {
            items: ({ query }: { query: string }) => {
              if (!query || query.length < 1) return [];
              return this.onTagSearch?.(query) || [];
            },
            render: () => {
              return {
                onStart: (props: MentionSuggestionProps) => {
                  this.currentSuggestionProps = props;
                  this.currentSelectedIndex = 0;
                  const rect =
                    typeof props.clientRect === "function"
                      ? props.clientRect()
                      : props.clientRect;
                  if (rect) {
                    this.startAutocomplete(props.query, rect);
                  }
                },
                onUpdate: (props: MentionSuggestionProps) => {
                  this.currentSuggestionProps = props;
                  const rect =
                    typeof props.clientRect === "function"
                      ? props.clientRect()
                      : props.clientRect;
                  if (rect) {
                    this.updateAutocomplete(props.query, rect);
                  }
                },
                onExit: () => {
                  this.currentSuggestionProps = null;
                  this.currentSelectedIndex = 0;
                  this.stopAutocomplete();
                },
                onKeyDown: (props: MentionKeyDownProps) => {
                  if (!this.currentSuggestionProps) return false;

                  // Handle navigation and selection
                  if (props.event.key === "ArrowDown") {
                    const items = this.currentSuggestionProps
                      .items as unknown as TagSearchResult[];
                    this.currentSelectedIndex = Math.min(
                      this.currentSelectedIndex + 1,
                      (items?.length || 1) - 1,
                    );
                    this.updateAutocompleteSelection();
                    return true;
                  }
                  if (props.event.key === "ArrowUp") {
                    this.currentSelectedIndex = Math.max(
                      this.currentSelectedIndex - 1,
                      0,
                    );
                    this.updateAutocompleteSelection();
                    return true;
                  }
                  if (props.event.key === "Enter") {
                    const items = this.currentSuggestionProps
                      .items as unknown as TagSearchResult[];
                    const searchResult = items[this.currentSelectedIndex];
                    if (searchResult) {
                      this.currentSuggestionProps.command({
                        id: searchResult.tag.id,
                        label: searchResult.tag.name,
                      });
                      return true;
                    } else {
                      const newTag = this.onTagCreate?.(this.currentSuggestionProps.query);
                      if (newTag) {
                        this.currentSuggestionProps.command({
                          id: newTag.tag.id,
                          label: newTag.tag.name,
                        });
                      }
                      return true;
                    }
                  }
                  if (props.event.key === "Escape") {
                    this.stopAutocomplete();
                    props.event.stopPropagation();
                    return true;
                  }

                  return false;
                },
              };
            },
          },
        }),
      ],
      content: "",
      editorProps: {
        attributes: {
          class: "prose prose-sm max-w-none focus:outline-none",
          spellcheck: "false",
        },
      },
      // Note: onUpdate, onCreate, onDestroy will be overridden by the hook
    };
  }

  /**
   * Set editor instance (called from hook)
   */
  public setEditor(editor: Editor | null) {
    this.editor = editor;
  }

  /**
   * Handle editor content changes (called from hook's onUpdate)
   */
  public handleContentChange(editor: Editor) {
    // Save rich content as JSON to preserve mentions and formatting
    const jsonContent = JSON.stringify(editor.getJSON());
    this.saveDraftText(jsonContent);
  }

  /**
   * Start autocomplete
   */
  private startAutocomplete(query: string, rect: DOMRect) {
    const results = this.onTagSearch?.(query) || [];

    const newState: TagAutocompleteState = {
      isOpen: true,
      query,
      results,
      selectedIndex: 0,
      position: { x: rect.left, y: rect.bottom },
      placement: {
        horizontal: "left",
        vertical: "below",
      },
    };

    // Only update if state changed
    if (!this.currentAutocompleteState || 
        this.currentAutocompleteState.query !== query ||
        this.currentAutocompleteState.position.x !== rect.left ||
        this.currentAutocompleteState.position.y !== rect.bottom) {
      this.currentAutocompleteState = newState;
      this.currentSelectedIndex = 0;
      this.onStateChange?.(newState);
    }
  }

  /**
   * Update autocomplete
   */
  private updateAutocomplete(query: string, rect: DOMRect) {
    const results = this.onTagSearch?.(query) || [];

    const newState: TagAutocompleteState = {
      isOpen: true,
      query,
      results,
      selectedIndex: this.currentSelectedIndex, // Preserve current selection
      position: { x: rect.left, y: rect.bottom },
      placement: {
        horizontal: "left",
        vertical: "below",
      },
    };

    // Only update if state changed
    if (!this.currentAutocompleteState || 
        this.currentAutocompleteState.query !== query ||
        this.currentAutocompleteState.position.x !== rect.left ||
        this.currentAutocompleteState.position.y !== rect.bottom ||
        this.currentAutocompleteState.results.length !== results.length) {
      this.currentAutocompleteState = newState;
      this.onStateChange?.(newState);
    }
  }

  /**
   * Stop autocomplete
   */
  public stopAutocomplete() {
    const newState: TagAutocompleteState = {
      isOpen: false,
      query: "",
      results: [],
      selectedIndex: 0,
      position: { x: 0, y: 0 },
      placement: {
        horizontal: "left",
        vertical: "below",
      },
    };

    // Clear stored state
    this.currentAutocompleteState = null;
    this.currentSelectedIndex = 0;
    this.onStateChange?.(newState);
  }

  /**
   * Handle tag selection from custom autocomplete UI
   * Uses the actual selected result to insert the tag through Tiptap's suggestion system
   */
  public handleTagSelect(result: TagSearchResult): boolean {
    if (!this.currentSuggestionProps) {
      console.warn("No active suggestion props available for tag selection");
      this.stopAutocomplete();
      return false;
    }

    try {
      // Use Tiptap's suggestion command with the actual selected result
      this.currentSuggestionProps.command({
        id: result.tag.id,
        label: result.tag.name,
      });

      this.stopAutocomplete();
      return true;
    } catch (error) {
      console.error("Failed to insert tag through suggestion system:", error);
      this.stopAutocomplete();
      return false;
    }
  }

  /**
   * Handle tag selection by index (for click events)
   */
  public handleTagSelectByIndex(index: number): boolean {
    if (!this.currentSuggestionProps) {
      console.warn("No active suggestion props available for tag selection");
      this.stopAutocomplete();
      return false;
    }

    const items = this.currentSuggestionProps
      .items as unknown as TagSearchResult[];
    const result = items[index];

    if (!result) {
      console.warn("No tag result found at index", index);
      return false;
    }

    // Update the selected index and select the tag
    this.currentSelectedIndex = index;
    return this.handleTagSelect(result);
  }

  /**
   * Handle creating new tag
   */
  public handleCreateTag(name: string): TagSearchResult | null {
    return this.onTagCreate?.(name) || null;
  }

  /**
   * Clear editor text
   */
  public clearText() {
    if (this.editor) {
      this.editor.commands.clearContent();
    }
  }

  /**
   * Get current text from editor
   */
  public getText(): string {
    return this.editor?.getText() || "";
  }

  /**
   * Get content with proper Tag boundaries preserved
   * Converts Tiptap mention nodes back to our Tag format for message sending
   */
  public getContentWithTags(): string {
    if (!this.editor) return "";

    const doc = this.editor.getJSON();
    return this.convertDocumentToTaggedText(doc);
  }

  /**
   * Convert Tiptap JSON document to text with Tag boundaries preserved
   */
  private convertDocumentToTaggedText(doc: TiptapNode): string {
    if (!doc || !doc.content) return "";

    let result = "";

    for (const node of doc.content) {
      result += this.convertNodeToText(node);
    }

    return result.trim();
  }

  /**
   * Convert individual Tiptap node to text with Tag preservation
   */
  private convertNodeToText(node: TiptapNode): string {
    if (!node) return "";

    // Handle mention nodes - convert back to our Tag format
    if (node.type === "mention" && this.isMentionNode(node)) {
      const mentionNode = node as TiptapMentionNode;
      if (mentionNode.attrs) {
        const tagId = mentionNode.attrs.id;
        const tagLabel = mentionNode.attrs.label;
        if (tagId && tagLabel) {
          return `<Tag:${tagId}>${tagLabel}</Tag>`;
        }
        return `@${tagLabel || tagId || "unknown"}`;
      }
    }

    // Handle text nodes
    if (node.type === "text" && this.isTextNode(node)) {
      const textNode = node as TiptapTextNode;
      return textNode.text || "";
    }

    // Handle paragraph and other container nodes
    if (node.content) {
      let result = "";
      for (const childNode of node.content) {
        result += this.convertNodeToText(childNode);
      }

      // Add newline for paragraph breaks (except for the last one)
      if (node.type === "paragraph") {
        result += "\n";
      }

      return result;
    }

    return "";
  }

  /**
   * Type guard for mention nodes
   */
  private isMentionNode(node: TiptapNode): node is TiptapMentionNode {
    return node.type === "mention" && "attrs" in node && !!node.attrs;
  }

  /**
   * Type guard for text nodes
   */
  private isTextNode(node: TiptapNode): node is TiptapTextNode {
    return (
      node.type === "text" && "text" in node && typeof node.text === "string"
    );
  }

  /**
   * Set text in editor
   */
  public setText(text: string) {
    if (this.editor) {
      try {
        // Try to parse as JSON first (rich content with mentions)
        const jsonContent = JSON.parse(text);
        this.editor.commands.setContent(jsonContent);
      } catch {
        // If not JSON, treat as plain text
        this.editor.commands.setContent(text);
      }
    }
  }

  /**
   * Focus the editor
   */
  public focus() {
    if (this.editor) {
      this.editor.commands.focus();
    }
  }

  /**
   * Get editor instance
   */
  public getEditor(): Editor | null {
    return this.editor;
  }

  private updateAutocompleteSelection() {
    if (this.onStateChange && this.currentAutocompleteState) {
      // Update the existing autocomplete state with new selectedIndex
      const newState: TagAutocompleteState = {
        ...this.currentAutocompleteState,
        selectedIndex: this.currentSelectedIndex,
      };
      this.onStateChange(newState);
    }
  }
}

// Export singleton instance
export const tiptapTextInputHandler = TiptapTextInputHandler.getInstance();
